import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import IconButton from '@mui/material/IconButton'
import { ReactComponent as PanelIcon } from '../../assets/images/responsive-design-expand-1.svg'
import { ReactComponent as EditIcon } from '../../assets/images/Edit_Icon.svg'
import { ReactComponent as CloseIcon } from '../../assets/images/Close.svg'
import { ReactComponent as CommentsIcon } from '../../assets/images/messages-bubble-add.svg'
import { ReactComponent as ValueCompendiumIcon } from '../../assets/images/value-compendium-logo.svg'
import Grid from '@mui/material/Grid'
import Divider from '@mui/material/Divider'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContentText from '@mui/material/DialogContentText'
import '../../style/detailed-view.scss'
import ActivityDetailedView from '../Activities/ActivityDetailedView'
import VMDetailedView from '../ValueMessages/VMDetailedView'
import EvidenceDetailedView from '../Evidence/EvidenceDetailedView'
import StrategicImperativeDetailedView from '../StrategicImperatives/StrategicImperativeDetailedView'
import { useSelector } from 'react-redux'
import * as _ from 'underscore'
import { Badge, Button, SvgIcon, Typography } from '@mui/material'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import Link from '@mui/material/Link'
import { ArrowBackIos } from '@mui/icons-material'
import { ReactComponent as MinusCircleIcon } from '../../assets/images/MinusCircle.svg'
import Tooltip from '@mui/material/Tooltip'
import { Comments } from '../../shared/components/Comments'
import { useCommentsQueries } from '../../shared/components/Comments/useCommentsQueries'
import { useIsDarkMode } from '../../shared/hooks/useIsDarkMode'
import Stack from '@mui/material/Stack'
import { useIsMobileDevice } from '../../shared/hooks/useIsMobileDevice'
import { useIsRoadmapReadonly } from '../../shared/hooks/useIsRoadmapReadonly'
import { useStrategy } from '../../shared/hooks/useStrategy'
import { useUnimportActivityMutation } from '../../shared/queries/useUnimportActivityMutation'
import useInvalidateLinkingQueries from '../../shared/queries/linking/useInvalidateLinkingQueries'

export default function TablePanel({ data, type, closePanel, openPanelAction, updateParent, navigationStack }) {
    const [openPanel, setOpenPanel] = useState(false)
    const [editing, setEditing] = useState(false)
    const [isCommentsOpen, setIsCommentsOpen] = useState(false)
    const snapshotMode = useSelector((state) => (state.strategy ? !!state.strategy.sourceId : false))
    const userReadonly = useIsRoadmapReadonly()
    const [triggerSave, setTriggerSave] = useState(0)
    const [triggerDelete, setTriggerDelete] = useState(0)
    const [triggerCancel, setTriggerCancel] = useState(0)
    const [openUnimportModal, setOpenUnimportModal] = useState(false)

    const { getCommentsQuery } = useCommentsQueries({ dataId: data._id, dataType: type })
    const commentsCount = getCommentsQuery.data?.length ?? 0
    const isDarkMode = useIsDarkMode()
    const strategy = useStrategy()
    const { mutateAsync: unimportActivityMutation } = useUnimportActivityMutation()
    const { invalidateLinkingQueries } = useInvalidateLinkingQueries()

    const history = useHistory()

    const toggleDrawer = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return
        }
        setOpenPanel(open)
    }

    async function unimportTheActivity() {
        setOpenUnimportModal(false)
        await unimportActivityMutation({ activityId: data._id, roadmapId: strategy._id, activityData: data })
        // unimportActivity unlinks everything this roadmap linked to the Activity server-side
        // (same mechanism as deleteActivity), so the linking cache needs the same reset.
        invalidateLinkingQueries()
        updateParent(true)
        handlePanelClose()
    }

    function handlePanelClose() {
        toggleDrawer(false)
        closePanel()
    }

    useEffect(() => {
        if (openPanelAction) {
            setOpenPanel(true)
        }
    }, [])

    const dialogTitleRef = useRef(null)
    const [dialogTitleHeight, setDialogTitleHeight] = useState(0)
    const dialogContentHeight = `calc(100% - ${dialogTitleHeight}px)`

    useEffect(() => {
        if (dialogTitleRef.current) {
            setDialogTitleHeight(dialogTitleRef.current.offsetHeight)
        }
    }, [openPanel])

    const isMobileDevice = useIsMobileDevice()

    return (
        <>
            <IconButton onClick={toggleDrawer(true)}>
                <PanelIcon />
            </IconButton>
            <SwipeableDrawer
                transitionDuration={400}
                anchor="right"
                open={openPanel}
                onClose={handlePanelClose}
                onOpen={toggleDrawer(true)}
                PaperProps={{
                    sx: {
                        width: isMobileDevice ? '100%' : isCommentsOpen ? '95%' : '80%',
                        backgroundColor: isDarkMode ? '#363943' : 'white',
                    },
                }}
            >
                <Box
                    sx={{
                        backgroundColor: isDarkMode ? '#363943' : 'white',
                        ...(isCommentsOpen ? { overflow: 'hidden', height: '100%' } : {}),
                    }}
                >
                    <DialogTitle sx={{ p: 2 }} id="customized-dialog-title" ref={dialogTitleRef}>
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid container item xs="auto">
                                {!_.isEmpty(navigationStack) ? (
                                    <Link
                                        component={RouterLink}
                                        underline="none"
                                        color="secondary"
                                        to={{
                                            pathname: navigationStack[navigationStack.length - 1].path,
                                            autoOpenData: {
                                                isAutoOpenPanel:
                                                    navigationStack[navigationStack.length - 1].isAutoOpenPanel ?? true,
                                                autoOpenData: navigationStack[navigationStack.length - 1].data,
                                                previousDataStack: navigationStack.slice(0, navigationStack.length - 1),
                                            },
                                        }}
                                    >
                                        <Tooltip
                                            enterDelay={600}
                                            enterNextDelay={600}
                                            title="Back to linked data"
                                            arrow
                                        >
                                            <IconButton>
                                                <ArrowBackIos color="primary" />
                                            </IconButton>
                                        </Tooltip>
                                    </Link>
                                ) : (
                                    <></>
                                )}
                            </Grid>
                            <Grid container alignItems="center" justifyContent="flex-end" xs="auto" spacing={1}>
                                <Grid item xs="auto">
                                    {editing ? (
                                        // ActivityForm owns its own Cancel/Save/Delete buttons; nothing to render here for activities
                                        type !== 'activity' && (
                                            <Grid container item spacing={1}>
                                                <Grid item>
                                                    <Button
                                                        onClick={() => setTriggerDelete((prev) => prev + 1)}
                                                        aria-label="delete"
                                                        variant="no-outline-sm-button"
                                                    >
                                                        Delete
                                                    </Button>
                                                </Grid>
                                                <Grid item>
                                                    <Button
                                                        onClick={() => setTriggerCancel((prev) => prev + 1)}
                                                        aria-label="cancel"
                                                        variant="outlined-sm-button"
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Grid>
                                                <Grid item>
                                                    <Button
                                                        onClick={() => setTriggerSave((prev) => prev + 1)}
                                                        aria-label="save"
                                                        variant="contained-sm-button"
                                                    >
                                                        Save
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        )
                                    ) : (
                                        <Grid container item xs="auto" spacing={1}>
                                            {type === 'valueMessage' &&
                                                !data.isCountrySpecific &&
                                                !data?.narrative?.archived && (
                                                    <Grid item>
                                                        <Tooltip
                                                            placement="left"
                                                            arrow
                                                            title={
                                                                <Stack gap={1}>
                                                                    <Typography
                                                                        variant="14x400"
                                                                        color="figma.grayscale.textHier1"
                                                                        style={{
                                                                            whiteSpace: 'pre-line',
                                                                        }}
                                                                    >
                                                                        View in{' '}
                                                                        <Typography
                                                                            variant="14x600"
                                                                            color="figma.primary"
                                                                        >
                                                                            Value Compendium
                                                                        </Typography>
                                                                    </Typography>
                                                                </Stack>
                                                            }
                                                            componentsProps={{
                                                                tooltip: {
                                                                    sx: {
                                                                        backgroundColor:
                                                                            'figma.grayscale.contentWrapper',
                                                                        px: 2,
                                                                        py: 0.5,
                                                                        borderRadius: '6px',
                                                                        boxShadow:
                                                                            '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                                                                    },
                                                                },
                                                                arrow: {
                                                                    sx: {
                                                                        color: 'figma.grayscale.contentWrapper',
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            <IconButton
                                                                aria-label="vc"
                                                                onClick={() => {
                                                                    history.push(
                                                                        `/vc/narratives/categories/${data.categoryId}/narratives/${data.narrativeId}/messages/${data._id}`
                                                                    )
                                                                }}
                                                                sx={{
                                                                    color: 'figma.grayscale.tier1',
                                                                }}
                                                                size="large"
                                                            >
                                                                <SvgIcon
                                                                    component={ValueCompendiumIcon}
                                                                    color="inherit"
                                                                    inheritViewBox
                                                                    fontSize="inherit"
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                )}
                                            <Grid item>
                                                <IconButton
                                                    aria-label="comments"
                                                    onClick={() => setIsCommentsOpen(!isCommentsOpen)}
                                                    size="large"
                                                >
                                                    <Badge
                                                        badgeContent={commentsCount}
                                                        color="primary"
                                                        overlap="circular"
                                                        invisible={!commentsCount}
                                                    >
                                                        <SvgIcon
                                                            component={CommentsIcon}
                                                            inheritViewBox
                                                            color="primary"
                                                            fontSize="inherit"
                                                        />
                                                    </Badge>
                                                </IconButton>
                                            </Grid>
                                            {!snapshotMode && !userReadonly && (
                                                <Grid item>
                                                    <Tooltip
                                                        title={
                                                            type === 'activity' && data.isOwner === false
                                                                ? `Edit mode is disabled. Please visit the source roadmap ${data.ownerRoadmapName ?? ''} to make changes to activity fields.`.trim()
                                                                : ''
                                                        }
                                                    >
                                                        <span>
                                                            <IconButton
                                                                aria-label="edit"
                                                                disabled={type === 'activity' && data.isOwner === false}
                                                                onClick={() => {
                                                                    setEditing(true)
                                                                    setIsCommentsOpen(false)
                                                                }}
                                                                size="large"
                                                                sx={{ '&.Mui-disabled': { opacity: 0.3 } }}
                                                            >
                                                                <SvgIcon
                                                                    component={EditIcon}
                                                                    inheritViewBox
                                                                    color="primary"
                                                                    fontSize="inherit"
                                                                />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Grid>
                                            )}
                                            {!snapshotMode &&
                                                !userReadonly &&
                                                type === 'activity' &&
                                                data.isOwner === false && (
                                                    <Grid item>
                                                        <Tooltip title="Remove this activity from your roadmap">
                                                            <IconButton
                                                                aria-label="unimport"
                                                                onClick={() => setOpenUnimportModal(true)}
                                                                size="large"
                                                            >
                                                                <SvgIcon
                                                                    component={MinusCircleIcon}
                                                                    inheritViewBox
                                                                    color="primary"
                                                                    fontSize="inherit"
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                )}
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid item xs="auto" mb={-0.5}>
                                    <IconButton
                                        onClick={handlePanelClose}
                                        onKeyDown={handlePanelClose}
                                        aria-label="close"
                                        size="large"
                                    >
                                        <SvgIcon
                                            component={CloseIcon}
                                            inheritViewBox
                                            color="primary"
                                            fontSize="inherit"
                                        />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogTitle>
                    <DialogContent
                        dividers
                        sx={{
                            backgroundColor: isDarkMode ? '#363943' : 'white',
                            borderBottom: 'none',
                            p: 0,
                            ...(isCommentsOpen ? { height: dialogContentHeight, overflow: 'hidden' } : {}),
                        }}
                    >
                        <Grid container sx={{ ...(isCommentsOpen ? { height: '100%', overflow: 'auto' } : {}) }}>
                            <Grid
                                container
                                item
                                xs={12}
                                md={isCommentsOpen ? 6 : 12}
                                lg={isCommentsOpen ? 8 : 12}
                                xl={isCommentsOpen ? 8.5 : 12}
                                sx={{
                                    ...(isCommentsOpen ? { height: '100%', overflow: 'auto', paddingRight: 2 } : {}),
                                }}
                            >
                                {type === 'activity' && (
                                    <ActivityDetailedView
                                        activity={data}
                                        forceParentRefresh={updateParent}
                                        closePanel={handlePanelClose}
                                        setEditing={setEditing}
                                        editing={editing}
                                        navigationStack={navigationStack}
                                    />
                                )}
                                {type === 'valueMessage' && (
                                    <VMDetailedView
                                        valueMessage={data}
                                        forceParentRefresh={updateParent}
                                        closePanel={handlePanelClose}
                                        setEditing={setEditing}
                                        editing={editing}
                                        triggerSave={triggerSave}
                                        triggerDelete={triggerDelete}
                                        triggerCancel={triggerCancel}
                                        navigationStack={navigationStack}
                                        isCommentsOpen={isCommentsOpen}
                                        dialogTitleHeight={dialogTitleHeight}
                                    />
                                )}
                                {type === 'evidence' && (
                                    <EvidenceDetailedView
                                        evidence={data}
                                        forceParentRefresh={updateParent}
                                        closePanel={handlePanelClose}
                                        setEditing={setEditing}
                                        editing={editing}
                                        triggerSave={triggerSave}
                                        triggerDelete={triggerDelete}
                                        triggerCancel={triggerCancel}
                                        navigationStack={navigationStack}
                                    />
                                )}
                                {type === 'strategicImperative' && (
                                    <StrategicImperativeDetailedView
                                        strategicImperative={data}
                                        forceParentRefresh={updateParent}
                                        closePanel={handlePanelClose}
                                        setEditing={setEditing}
                                        editing={editing}
                                        triggerSave={triggerSave}
                                        triggerDelete={triggerDelete}
                                        triggerCancel={triggerCancel}
                                        navigationStack={navigationStack}
                                    />
                                )}
                            </Grid>
                            {isCommentsOpen && (
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    md={isCommentsOpen ? 6 : 12}
                                    lg={isCommentsOpen ? 4 : 12}
                                    xl={isCommentsOpen ? 3.5 : 12}
                                    sx={{ ...(isCommentsOpen ? { height: '100%', overflow: 'auto' } : {}) }}
                                    order={{ xs: -1, md: 0 }}
                                >
                                    <Comments dataId={data._id} dataType={type} />
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    <Divider style={{ border: 'none' }} />
                </Box>
            </SwipeableDrawer>
            <Dialog
                open={openUnimportModal}
                onClose={() => setOpenUnimportModal(false)}
                aria-labelledby="alert-dialog-unimport-title"
            >
                <DialogTitle id="alert-dialog-unimport-title">
                    <Typography variant="h4">
                        Are you sure you want to remove this activity from your roadmap?
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are removing the activity from the current roadmap. You'll still be able to re-add it later.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUnimportModal(false)} sx={{ mb: 1 }} variant="outlined-sm-button">
                        CANCEL
                    </Button>
                    <Button onClick={unimportTheActivity} sx={{ mb: 1, mr: 1 }} variant="contained-sm-button">
                        REMOVE
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
